import type { FormEvent } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Select } from "@bafe/ui";
import { CUSTOMER_DATABASE_PRIVILEGES } from "@bafe/validation";

import { Popup } from "../../components/Popup";
import { api } from "../../lib/api";
import { presentError } from "../../lib/errors";
import { queryKeys } from "../../lib/query";

type Access = "read" | "write" | "full";

const ACCESS: Array<{ id: Access; label: string; privileges: readonly string[] }> = [
	{ id: "read", label: "Read", privileges: ["SELECT"] },
	{ id: "write", label: "Read and write", privileges: ["SELECT", "INSERT", "UPDATE", "DELETE"] },
	{ id: "full", label: "Full", privileges: CUSTOMER_DATABASE_PRIVILEGES },
];

function privilegesFor(access: Access): string[] {
	return [...(ACCESS.find((item) => item.id === access)?.privileges ?? ["SELECT"])];
}

function accessOf(privileges: readonly string[]): Access | "custom" {
	const same = (expected: readonly string[]) =>
		expected.length === privileges.length && expected.every((item) => privileges.includes(item));
	if (same(ACCESS[2]?.privileges ?? [])) return "full";
	if (same(ACCESS[1]?.privileges ?? [])) return "write";
	if (same(ACCESS[0]?.privileges ?? [])) return "read";
	return "custom";
}

function accessLabel(privileges: readonly string[]): string {
	const level = accessOf(privileges);
	return ACCESS.find((item) => item.id === level)?.label ?? "Custom";
}

function suggestPassword(): string {
	const alphabet = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
	const bytes = new Uint8Array(18);
	crypto.getRandomValues(bytes);
	return Array.from(bytes, (byte) => alphabet[byte % alphabet.length] ?? "a").join("");
}

function closeMenu(target: EventTarget) {
	if (target instanceof Element) target.closest("details")?.removeAttribute("open");
}

export function SiteDatabases() {
	const client = useQueryClient();
	const databases = useQuery({ queryKey: queryKeys.siteDatabases, queryFn: () => api.listCustomerDatabases() });
	const [databaseOpen, setDatabaseOpen] = useState(false);
	const [userOpen, setUserOpen] = useState(false);
	const [name, setName] = useState("");
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const [revealed, setRevealed] = useState(false);
	const [assigning, setAssigning] = useState<string | null>(null);
	const [role, setRole] = useState("");
	const [access, setAccess] = useState<Access>("write");
	const [deleting, setDeleting] = useState<string | null>(null);
	const [editingUser, setEditingUser] = useState<string | null>(null);
	const [deletingUser, setDeletingUser] = useState<string | null>(null);
	const [deleteConfirm, setDeleteConfirm] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [popupError, setPopupError] = useState<string | null>(null);
	const [note, setNote] = useState<string | null>(null);

	const refresh = () => client.invalidateQueries({ queryKey: queryKeys.siteDatabases });

	const createDatabase = useMutation({
		mutationFn: () => api.createCustomerDatabase(name),
		onSuccess: async (created) => {
			setName("");
			setDatabaseOpen(false);
			setPopupError(null);
			setNote(`${created.name} was created.`);
			setError(null);
			await refresh();
		},
		onError: (caught) => setPopupError(presentError(caught).title),
	});
	const createUser = useMutation({
		mutationFn: () => api.createCustomerDatabaseRole({ name: username, password, passwordConfirm: confirm }),
		onSuccess: async (created) => {
			setUsername("");
			setPassword("");
			setConfirm("");
			setRevealed(false);
			setUserOpen(false);
			setPopupError(null);
			setNote(`${created.name} was created. The password was not saved.`);
			setError(null);
			await refresh();
		},
		onError: (caught) => setPopupError(presentError(caught).title),
	});
	const editUser = useMutation({
		mutationFn: (target: string) => api.updateCustomerDatabaseRole(target, { password, passwordConfirm: confirm }),
		onSuccess: async (updated) => {
			setPassword("");
			setConfirm("");
			setRevealed(false);
			setEditingUser(null);
			setPopupError(null);
			setNote(`${updated.name} was updated. The new password was not saved.`);
			setError(null);
			await refresh();
		},
		onError: (caught) => setPopupError(presentError(caught).title),
	});
	const removeUser = useMutation({
		mutationFn: (target: string) => api.deleteCustomerDatabaseRole(target, deleteConfirm),
		onSuccess: async () => {
			setDeletingUser(null);
			setDeleteConfirm("");
			setPopupError(null);
			setNote("The user was deleted.");
			setError(null);
			await refresh();
		},
		onError: (caught) => setPopupError(presentError(caught).title),
	});
	const removeDatabase = useMutation({
		mutationFn: (target: string) => api.deleteCustomerDatabase(target, deleteConfirm),
		onSuccess: async () => {
			setDeleting(null);
			setDeleteConfirm("");
			setPopupError(null);
			setNote("The database was deleted.");
			setError(null);
			await refresh();
		},
		onError: (caught) => setPopupError(presentError(caught).title),
	});
	const assign = useMutation({
		mutationFn: async (input: { database: string; role: string; current: string[]; next: string[] }) => {
			const add = input.next.filter((item) => !input.current.includes(item));
			const remove = input.current.filter((item) => !input.next.includes(item));
			if (add.length === 0 && remove.length === 0) return;
			if (add.length > 0) await api.grantCustomerDatabasePrivileges(input.database, { role: input.role, privileges: add });
			if (remove.length > 0) await api.revokeCustomerDatabasePrivileges(input.database, { role: input.role, privileges: remove });
		},
		onSuccess: async () => {
			setAssigning(null);
			setPopupError(null);
			setNote("Access was updated.");
			setError(null);
			await refresh();
		},
		onError: (caught) => setPopupError(presentError(caught).title),
	});

	function closeDatabase() {
		if (createDatabase.isPending) return;
		setDatabaseOpen(false);
		setPopupError(null);
	}

	function closeUser() {
		if (createUser.isPending) return;
		setUserOpen(false);
		setPopupError(null);
		setPassword("");
		setConfirm("");
		setRevealed(false);
	}

	function onCreateDatabase(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (name.trim() === "") {
			setPopupError("Enter a database name.");
			return;
		}
		createDatabase.mutate();
	}

	function onCreateUser(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (username.trim() === "" || password === "" || confirm === "") {
			setPopupError("Enter a username and the password twice.");
			return;
		}
		createUser.mutate();
	}

	if (databases.isLoading) return <p className="empty">Loading databases.</p>;
	if (databases.isError) return <p className="err">{presentError(databases.error).title}</p>;
	const data = databases.data;
	if (!data) return null;
	const ready = data.configured;

	return (
		<div className="blocks">
			{!ready && <p className="err">{data.message}</p>}
			{error && <p className="err">{error}</p>}
			{note && <p className="sub">{note}</p>}
			<section>
				<div className="row">
					<h2>Databases</h2>
					<button className="btn" type="button" disabled={!ready} onClick={() => { setPopupError(null); setDatabaseOpen(true); }}>
						New database
					</button>
				</div>
				<div className="table-wrap">
					<table>
						<caption className="sr-only">SQL databases</caption>
						<thead>
							<tr>
								<th>Database</th>
								<th>Size</th>
								<th>Users</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{data.databases.length === 0 && (
								<tr>
									<td colSpan={4}>No databases yet.</td>
								</tr>
							)}
							{data.databases.map((item) => (
								<tr key={item.name}>
									<td>{item.name}</td>
									<td>{item.size}</td>
									<td className="quiet">{item.roles.length > 0 ? item.roles.join(", ") : "None"}</td>
									<td className="menu-cell">
										{(item.canManage || item.canDelete) && (
											<details className="menu">
												<summary aria-label={`Actions for ${item.name}`}>
													<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
														<circle cx="12" cy="5" r="1.8" />
														<circle cx="12" cy="12" r="1.8" />
														<circle cx="12" cy="19" r="1.8" />
													</svg>
												</summary>
												<div className="select-pop">
													{item.canManage && (
														<button
															className="select-item"
															type="button"
															disabled={!ready || data.roles.length === 0}
															onClick={(event) => {
																closeMenu(event.target);
																const first = item.roles[0] ?? data.roles[0] ?? "";
																const grant = item.grants.find((entry) => entry.role === first);
																const level = grant ? accessOf(grant.privileges) : "write";
																setRole(first);
																setAccess(level === "custom" ? "write" : level);
																setPopupError(null);
																setAssigning(item.name);
															}}
														>
															Assign
														</button>
													)}
													{item.canDelete && (
														<button
															className="select-item"
															type="button"
															onClick={(event) => {
																closeMenu(event.target);
																setDeleting(item.name);
																setDeleteConfirm("");
																setPopupError(null);
															}}
														>
															Delete
														</button>
													)}
												</div>
											</details>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</section>
			<section>
				<div className="row">
					<h2>Users</h2>
					<button className="btn" type="button" disabled={!ready} onClick={() => { setPopupError(null); setUserOpen(true); }}>
						New user
					</button>
				</div>
				<div className="table-wrap">
					<table>
						<caption className="sr-only">Database users</caption>
						<thead>
							<tr>
								<th>User</th>
								<th>Access</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{data.roles.length === 0 && (
								<tr>
									<td colSpan={3}>No users yet.</td>
								</tr>
							)}
							{data.roles.map((roleName) => {
								const places = data.databases.flatMap((item) => {
									const grant = item.grants.find((entry) => entry.role === roleName);
									if (!grant && !item.roles.includes(roleName)) return [];
									return [`${item.name} · ${grant ? accessLabel(grant.privileges) : "Connected"}`];
								});
								return (
									<tr key={roleName}>
										<td>{roleName}</td>
										<td>{places.length > 0 ? places.join(", ") : "No database yet"}</td>
										<td className="menu-cell">
											<details className="menu">
												<summary aria-label={`Actions for ${roleName}`}>
													<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
														<circle cx="12" cy="5" r="1.8" />
														<circle cx="12" cy="12" r="1.8" />
														<circle cx="12" cy="19" r="1.8" />
													</svg>
												</summary>
												<div className="select-pop">
													<button
														className="select-item"
														type="button"
														onClick={(event) => {
															closeMenu(event.target);
															setPassword("");
															setConfirm("");
															setRevealed(false);
															setPopupError(null);
															setEditingUser(roleName);
														}}
													>
														Edit
													</button>
													<button
														className="select-item"
														type="button"
														onClick={(event) => {
															closeMenu(event.target);
															setDeleteConfirm("");
															setPopupError(null);
															setDeletingUser(roleName);
														}}
													>
														Delete
													</button>
												</div>
											</details>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</section>
			<Popup open={databaseOpen} title="New database" onClose={closeDatabase}>
				<form onSubmit={onCreateDatabase}>
					{popupError && databaseOpen && <p className="err">{popupError}</p>}
					<label htmlFor="database-name">Name</label>
					<input
						id="database-name"
						value={name}
						onChange={(event) => setName(event.target.value)}
						autoComplete="off"
						spellCheck={false}
						autoFocus
					/>
					<p className="actions">
						<button className="btn" type="submit" disabled={createDatabase.isPending}>
							{createDatabase.isPending ? "Creating" : "Create database"}
						</button>
						<button className="btn ghost" type="button" onClick={closeDatabase}>
							Cancel
						</button>
					</p>
				</form>
			</Popup>
			<Popup open={userOpen} title="New user" onClose={closeUser}>
				<form onSubmit={onCreateUser}>
					{popupError && userOpen && <p className="err">{popupError}</p>}
					<p className="sub">The password is set once and is not stored.</p>
					<label htmlFor="database-user">Username</label>
					<input
						id="database-user"
						value={username}
						onChange={(event) => setUsername(event.target.value)}
						autoComplete="off"
						spellCheck={false}
						autoFocus
					/>
					<label htmlFor="database-password">Password</label>
					<input
						id="database-password"
						type={revealed ? "text" : "password"}
						value={password}
						onChange={(event) => setPassword(event.target.value)}
						autoComplete="new-password"
					/>
					<label htmlFor="database-password-again">Password again</label>
					<input
						id="database-password-again"
						type={revealed ? "text" : "password"}
						value={confirm}
						onChange={(event) => setConfirm(event.target.value)}
						autoComplete="new-password"
					/>
					<p className="actions">
						<button
							className="btn ghost"
							type="button"
							onClick={() => {
								const next = suggestPassword();
								setPassword(next);
								setConfirm(next);
								setRevealed(true);
							}}
						>
							Suggest a password
						</button>
						<button className="btn" type="submit" disabled={createUser.isPending}>
							{createUser.isPending ? "Creating" : "Create user"}
						</button>
						<button className="btn ghost" type="button" onClick={closeUser}>
							Cancel
						</button>
					</p>
				</form>
			</Popup>
			<Popup
				open={editingUser !== null}
				title="Edit user"
				onClose={() => {
					if (editUser.isPending) return;
					setEditingUser(null);
					setPassword("");
					setConfirm("");
					setRevealed(false);
					setPopupError(null);
				}}
			>
				<form
					onSubmit={(event) => {
						event.preventDefault();
						if (!editingUser) return;
						if (password === "" || confirm === "") {
							setPopupError("Enter the password twice.");
							return;
						}
						editUser.mutate(editingUser);
					}}
				>
					{popupError && editingUser && <p className="err">{popupError}</p>}
					<p className="sub">{editingUser}. The new password is set once and is not stored.</p>
					<label htmlFor="edit-password">Password</label>
					<input
						id="edit-password"
						type={revealed ? "text" : "password"}
						value={password}
						onChange={(event) => setPassword(event.target.value)}
						autoComplete="new-password"
						autoFocus
					/>
					<label htmlFor="edit-password-again">Password again</label>
					<input
						id="edit-password-again"
						type={revealed ? "text" : "password"}
						value={confirm}
						onChange={(event) => setConfirm(event.target.value)}
						autoComplete="new-password"
					/>
					<p className="actions">
						<button
							className="btn ghost"
							type="button"
							onClick={() => {
								const next = suggestPassword();
								setPassword(next);
								setConfirm(next);
								setRevealed(true);
							}}
						>
							Suggest a password
						</button>
						<button className="btn" type="submit" disabled={editUser.isPending}>
							{editUser.isPending ? "Saving" : "Save"}
						</button>
						<button
							className="btn ghost"
							type="button"
							onClick={() => {
								setEditingUser(null);
								setPassword("");
								setConfirm("");
								setRevealed(false);
								setPopupError(null);
							}}
						>
							Cancel
						</button>
					</p>
				</form>
			</Popup>
			<Popup
				open={deletingUser !== null}
				title="Delete user"
				onClose={() => {
					if (removeUser.isPending) return;
					setDeletingUser(null);
					setPopupError(null);
				}}
			>
				<form
					onSubmit={(event) => {
						event.preventDefault();
						if (deletingUser) removeUser.mutate(deletingUser);
					}}
				>
					{popupError && deletingUser && <p className="err">{popupError}</p>}
					<p className="sub">Type DELETE to remove {deletingUser}. This cannot be undone.</p>
					<label className="sr-only" htmlFor="delete-user">
						Type DELETE
					</label>
					<input
						id="delete-user"
						value={deleteConfirm}
						onChange={(event) => setDeleteConfirm(event.target.value)}
						placeholder="DELETE"
						autoComplete="off"
						autoFocus
					/>
					<p className="actions">
						<button className="btn" type="submit" disabled={removeUser.isPending || deleteConfirm !== "DELETE"}>
							{removeUser.isPending ? "Deleting" : "Delete"}
						</button>
						<button
							className="btn ghost"
							type="button"
							onClick={() => {
								setDeletingUser(null);
								setPopupError(null);
							}}
						>
							Cancel
						</button>
					</p>
				</form>
			</Popup>
			<Popup
				open={assigning !== null}
				title="Assign a user"
				onClose={() => {
					if (assign.isPending) return;
					setAssigning(null);
					setPopupError(null);
				}}
			>
				<form
					onSubmit={(event) => {
						event.preventDefault();
						const database = data.databases.find((item) => item.name === assigning);
						if (!database || role === "") {
							setPopupError("Choose a user.");
							return;
						}
						const current = database.grants.find((grant) => grant.role === role)?.privileges ?? [];
						const next = privilegesFor(access);
						if (current.length === next.length && next.every((privilege) => current.includes(privilege))) {
							setPopupError("That access is already set.");
							return;
						}
						assign.mutate({ database: database.name, role, current, next });
					}}
				>
					{popupError && assigning && <p className="err">{popupError}</p>}
					<p className="sub">{assigning}</p>
					<label htmlFor="assign-user">User</label>
					<Select id="assign-user" value={role} onChange={(event) => setRole(event.target.value)}>
						<option value="">Choose a user</option>
						{data.roles.map((item) => (
							<option key={item} value={item}>
								{item}
							</option>
						))}
					</Select>
					<label htmlFor="assign-access">Access</label>
					<Select
						id="assign-access"
						value={access}
						onChange={(event) => setAccess(event.target.value as Access)}
					>
						{ACCESS.map((item) => (
							<option key={item.id} value={item.id}>
								{item.label}
							</option>
						))}
					</Select>
					<p className="actions">
						<button className="btn" type="submit" disabled={assign.isPending || data.roles.length === 0}>
							{assign.isPending ? "Saving" : "Assign"}
						</button>
						<button className="btn ghost" type="button" onClick={() => { setAssigning(null); setPopupError(null); }}>
							Cancel
						</button>
					</p>
				</form>
			</Popup>
			<Popup
				open={deleting !== null}
				title="Delete database"
				onClose={() => {
					if (removeDatabase.isPending) return;
					setDeleting(null);
					setPopupError(null);
				}}
			>
				<form
					onSubmit={(event) => {
						event.preventDefault();
						if (deleting) removeDatabase.mutate(deleting);
					}}
				>
					{popupError && deleting && <p className="err">{popupError}</p>}
					<p className="sub">Type DELETE to remove {deleting}. This cannot be undone.</p>
					<label className="sr-only" htmlFor="delete-database">
						Type DELETE
					</label>
					<input
						id="delete-database"
						value={deleteConfirm}
						onChange={(event) => setDeleteConfirm(event.target.value)}
						placeholder="DELETE"
						autoComplete="off"
						autoFocus
					/>
					<p className="actions">
						<button className="btn" type="submit" disabled={removeDatabase.isPending || deleteConfirm !== "DELETE"}>
							{removeDatabase.isPending ? "Deleting" : "Delete"}
						</button>
						<button
							className="btn ghost"
							type="button"
							onClick={() => {
								setDeleting(null);
								setPopupError(null);
							}}
						>
							Cancel
						</button>
					</p>
				</form>
			</Popup>
		</div>
	);
}
